import type { InfiniteTablePropColumns } from "@infinite-table/infinite-react";
import type {
	ScreenerColumn,
	ScreenerData,
	ScreenerSubField,
} from "epsm-api-types/models";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	formatValue,
	parseNumber,
} from "@/components/widgets/helpers/FormattingHelpers";
import { getAlign, getUnixTime } from "@/lib/utils";
import { getType } from "@/lib/utils/getType";
import { getDisplayTitle } from "../getDisplayTitle";

export const processNestedSubfields = (
	column: ScreenerColumn,
	subFields: ScreenerSubField[],
	parentName: string,
	parentTitle: string,
	newColumnOrder: string[],
	result: InfiniteTablePropColumns<ScreenerData>,
	getColumnSizing: (
		columnName: string,
	) =>
		| { minWidth: number }
		| { defaultWidth: number }
		| { defaultFlex: number },
) => {
	for (const subfield of subFields) {
		const fullColumnName = `${parentName}.${subfield.name}`;

		const { fullTitle, displayTitle } = getDisplayTitle(
			column,
			subfield,
			parentName,
			parentTitle,
		);

		if (subfield.subFields?.length) {
			processNestedSubfields(
				column,
				subfield.subFields,
				fullColumnName,
				fullTitle,
				newColumnOrder,
				result,
				getColumnSizing,
			);
		} else {
			if (!newColumnOrder.includes(fullColumnName)) {
				newColumnOrder.push(fullColumnName);
			}

			result[fullColumnName] = {
				field: fullColumnName,
				...getColumnSizing(fullColumnName),
				headerAlign: "start",
				align: getAlign(column.type),
				header: () => (
					<Tooltip>
						<TooltipTrigger asChild>
							<p className="text-ellipsis overflow-hidden whitespace-nowrap">
								{displayTitle}
							</p>
						</TooltipTrigger>
						<TooltipContent className="custom-fade-in">
							{displayTitle}
						</TooltipContent>
					</Tooltip>
				),
				type: getType(column.type),
				valueFormatter: ({ data, field }) => {
					if (
						data &&
						typeof data[column.name] === "object" &&
						data[column.name] !== null
					) {
						const columnWithSubFieldsPathArray = field?.toString()?.split(".");

						const getValue = (
							obj: Record<string, any>,
							pathArray: string[],
						) => {
							if (!pathArray.length) return;

							return pathArray.reduce((acc: any, key: string) => {
								if (acc && typeof acc === "object" && key in acc) {
									return acc[key];
								}
							}, obj);
						};

						let value = getValue(data, columnWithSubFieldsPathArray || []);

						if (column.type === "date") {
							if (value instanceof Date) {
								value = value.toISOString();
							} else if (typeof value !== "string") {
								return "";
							}

							return formatValue(
								getUnixTime(value),
								column.type,
								column.options,
							);
						}

						return formatValue(value, column.type, column.options);
					}
				},
				...(column.fncClassName && {
					className: ({ value }) =>
						column.fncClassName!(
							column.type === "num" ? (parseNumber(value) as number) : value,
						),
				}),
			};
		}
	}
};
